import { useState, useEffect, useRef } from 'react'
import classNames from 'classnames'
import { motion, AnimatePresence } from 'framer-motion'
import Arrow from './Arrow'
import type { CommonProps } from '../@types/common'
import type { ArrowPlacement } from './Arrow'
import type { ReactNode } from 'react'
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    shift,
    useHover,
    useFocus,
    useDismiss,
    useRole,
    useInteractions,
    FloatingPortal,
} from '@floating-ui/react'

export interface TooltipProps extends CommonProps {
    isOpen?: boolean
    placement?: ArrowPlacement
    title: string | ReactNode
    wrapperClass?: string
    tooltipStyle?: React.CSSProperties
}

const Tooltip = (props: TooltipProps) => {
    const {
        className,
        children,
        isOpen = false,
        placement = 'top',
        title,
        wrapperClass,
        tooltipStyle,
    } = props

    const [tooltipOpen, setTooltipOpen] = useState<boolean>(isOpen)
    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false)
    const observerRef = useRef<MutationObserver | null>(null)

    const tooltipBackground = 'gray-800'
    const tooltipDarkBackground = 'black'

    const defaultTooltipClass = `tooltip bg-${tooltipBackground} dark:bg-${tooltipDarkBackground}`

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setTooltipOpen,
        placement,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(7),
            flip({
                fallbackAxisSideDirection: 'start',
            }),
            shift(),
        ],
    })

    const hover = useHover(context, { move: false })
    const focus = useFocus(context)
    const dismiss = useDismiss(context)
    const role = useRole(context, { role: 'tooltip' })

    const { getReferenceProps, getFloatingProps } = useInteractions([
        hover,
        focus,
        dismiss,
        role,
    ])

    // Check if the child contains a dropdown that is open
    const shouldHideTooltip = () => {
        // Use the state that's updated by the observer
        return dropdownOpen
    }

    // Set up mutation observer to watch for dropdown state changes
    useEffect(() => {
        const referenceElement = refs.reference.current
        if (
            !referenceElement ||
            (typeof referenceElement === 'object' &&
                !('querySelector' in referenceElement))
        ) {
            return
        }
        // Check initially
        const dropdown = referenceElement.querySelector('.dropdown')
        if (dropdown) {
            setDropdownOpen(dropdown.getAttribute('data-state') === 'open')
            // Create observer to watch for changes
            observerRef.current = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (
                        mutation.type === 'attributes' &&
                        mutation.attributeName === 'data-state'
                    ) {
                        const isOpen =
                            (mutation.target as Element).getAttribute(
                                'data-state',
                            ) === 'open'
                        setDropdownOpen(isOpen)
                    }
                })
            })
            // Start observing
            observerRef.current.observe(dropdown, {
                attributes: true,
                attributeFilter: ['data-state'],
            })
        }
        // Cleanup
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect()
            }
        }
    }, [refs.reference])

    return (
        <>
            <span
                ref={refs.setReference}
                {...getReferenceProps()}
                className={classNames('tooltip-wrapper', wrapperClass)}
            >
                {children}
            </span>
            <FloatingPortal>
                {tooltipOpen && !shouldHideTooltip() && (
                    <AnimatePresence>
                        <motion.div
                            ref={refs.setFloating}
                            className={classNames(
                                defaultTooltipClass,
                                className,
                            )}
                            initial={{
                                opacity: 0,
                                visibility: 'hidden',
                            }}
                            animate={
                                tooltipOpen
                                    ? {
                                          opacity: 1,
                                          visibility: 'visible',
                                      }
                                    : {
                                          opacity: 0,
                                          visibility: 'hidden',
                                      }
                            }
                            transition={{
                                duration: 0.15,
                                type: 'tween',
                            }}
                            style={{ ...floatingStyles, ...tooltipStyle }}
                            {...getFloatingProps()}
                        >
                            <span>{title}</span>
                            <Arrow
                                placement={context.placement}
                                color={tooltipBackground}
                                colorDark={tooltipDarkBackground}
                            />
                        </motion.div>
                    </AnimatePresence>
                )}
            </FloatingPortal>
        </>
    )
}

Tooltip.displayName = 'Tooltip'

export default Tooltip
